import type { BreadcrumbItem } from '../resources/resource-page-shell';

/**
 * Shared breadcrumb prefix for pages nested under a single automation's runs:
 *
 *   Automations > [Workflow name] > Runs
 *
 * Both the runs list ({@link WorkflowRunsPage}) and the run detail page build on
 * this so the "Automations" label and the workflow/runs hrefs live in one place
 * instead of being copy-pasted per page. The trailing "Runs" crumb carries its
 * href unconditionally — the breadcrumb renderer ignores href on the last (current
 * page) crumb, so the runs list can use this trail as-is while the run detail page
 * appends its own crumbs after it.
 */
export const workflowRunsBreadcrumbs = ({
  orgSlug,
  workflowId,
  workflowName,
}: {
  orgSlug: string;
  workflowId: string;
  workflowName: string | null;
}): BreadcrumbItem[] => [
  { label: 'Automations', href: `/${orgSlug}/workflows` },
  {
    label: workflowName ?? 'Workflow',
    href: `/${orgSlug}/workflows/${workflowId}`,
  },
  { label: 'Runs', href: `/${orgSlug}/workflows/${workflowId}/runs` },
];

/** How much of a run id reads as an identifier without becoming the crumb. */
const SHORT_RUN_ID_LENGTH = 8;

/**
 * Crumb (and panel view title) for one run's detail view.
 *
 * Names both the view and the run it is showing, because "Run details" alone is
 * the same crumb for every run of a workflow, and a workflow has many. The id
 * lives here rather than beside the status badge in the header: the trail is
 * where you look to answer "which one am I on", and the header row has the
 * workflow name and status to carry.
 */
export const runDetailsCrumbLabel = (runId: string): string =>
  `Run details ${runId.slice(0, SHORT_RUN_ID_LENGTH)}`;
